/**
 * AdminTreasuryUSDTPage — CEO-only treasury panel.
 *
 * Shows the internal USDT ledger that gestores NEVER see:
 *   • net balance (intake + outflow + adjustments)
 *   • totals: intakes (provider UY → CEO), outflows (CEO → CU), spread
 *   • full append-only ledger with filters
 *   • manual adjustment form
 *
 * RBAC: admin only.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bitcoin, ArrowDownCircle, ArrowUpCircle, Loader2, History, Filter, Plus,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KindBadge = ({ kind }) => {
  const m = {
    intake:     { label: 'Intake',     klass: 'bg-emerald-100 text-emerald-700' },
    outflow:    { label: 'Outflow',    klass: 'bg-rose-100 text-rose-700' },
    adjustment: { label: 'Ajuste',     klass: 'bg-slate-200 text-slate-700' },
  };
  const c = m[kind] || { label: kind, klass: 'bg-slate-100 text-slate-600' };
  return <Badge className={`text-[10px] ${c.klass}`}>{c.label}</Badge>;
};

const AdminTreasuryUSDTPage = () => {
  const { getAuthHeaders } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [adjOpen, setAdjOpen] = useState(false);
  const [adj, setAdj] = useState({ amount: '', reason: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = filter === 'all' ? '' : `?kind=${filter}`;
      const [k, m] = await Promise.all([
        axios.get(`${API}/admin/treasury/usdt`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/treasury/usdt/movements${qs}`, { headers: getAuthHeaders() }),
      ]);
      setKpis(k.data.kpis);
      setItems(m.data.items || []);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Error al cargar tesorería'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, filter]);

  useEffect(() => { load(); }, [load]);

  const submitAdj = async (e) => {
    e.preventDefault();
    const n = Number(adj.amount);
    if (!n) { toast.error('Monto inválido'); return; }
    if (!adj.reason || adj.reason.length < 3) { toast.error('Motivo requerido (≥3 chars)'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/treasury/usdt/adjustment`,
        { amount: n, reason: adj.reason },
        { headers: getAuthHeaders() });
      toast.success('Ajuste registrado');
      setAdjOpen(false);
      setAdj({ amount: '', reason: '' });
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo ajustar'));
    } finally {
      setBusy(false);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <div className="space-y-6" data-testid="admin-treasury-usdt-page">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Bitcoin className="w-6 h-6 text-emerald-600" /> Tesorería USDT (interna)
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Holdings privados del CEO. El gestor jamás ve este balance — sólo ve sus saldos UYU/USD operativos.
          </p>
        </div>
        <Button onClick={() => setAdjOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="treasury-add-adjustment">
          <Plus className="w-4 h-4 mr-1" /> Ajuste manual
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-0 shadow-sm p-4" data-testid="kpi-net">
          <p className="text-[10px] uppercase text-slate-500">Balance neto USDT</p>
          <p className="text-2xl font-bold font-mono text-emerald-700">{fmt(kpis?.net_balance)}</p>
        </Card>
        <Card className="border-0 shadow-sm p-4" data-testid="kpi-intakes">
          <p className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><ArrowDownCircle className="w-3 h-3" /> Intakes (UY → CEO)</p>
          <p className="text-2xl font-bold font-mono text-emerald-700">+{fmt(kpis?.intake)}</p>
        </Card>
        <Card className="border-0 shadow-sm p-4" data-testid="kpi-outflows">
          <p className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><ArrowUpCircle className="w-3 h-3" /> Outflows (CEO → CU)</p>
          <p className="text-2xl font-bold font-mono text-rose-700">{fmt(kpis?.outflow)}</p>
        </Card>
        <Card className="border-0 shadow-sm p-4" data-testid="kpi-ops">
          <p className="text-[10px] uppercase text-slate-500">Operaciones</p>
          <p className="text-2xl font-bold font-mono text-slate-900">{kpis?.operations_count || 0}</p>
          <p className="text-[10px] text-slate-400">Entregado en local: {fmt(kpis?.delivered_total)}</p>
        </Card>
      </div>

      {/* Filter + ledger */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center justify-between">
          <p className="text-sm font-semibold flex items-center gap-2"><History className="w-4 h-4" /> Ledger USDT</p>
          <div className="flex items-center gap-2">
            <Filter className="w-3 h-3 text-slate-400" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="treasury-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="intake">Intakes</SelectItem>
                <SelectItem value="outflow">Outflows</SelectItem>
                <SelectItem value="adjustment">Ajustes</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {loading ? (
          <p className="p-8 text-center text-slate-400 text-sm"><Loader2 className="w-4 h-4 inline animate-spin" /> Cargando…</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-slate-400 text-sm">Sin movimientos.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b text-left text-[10px] text-slate-500 uppercase">
                <th className="p-2">Tipo</th>
                <th className="p-2 text-right">USDT</th>
                <th className="p-2">Operación</th>
                <th className="p-2">Gestor</th>
                <th className="p-2 text-right">Tasa</th>
                <th className="p-2 text-right">Local</th>
                <th className="p-2">Descripción</th>
                <th className="p-2">Fecha</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="p-2"><KindBadge kind={m.kind} /></td>
                  <td className={`p-2 text-right font-mono ${m.amount >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{m.amount >= 0 ? '+' : ''}{fmt(m.amount)}</td>
                  <td className="p-2 text-slate-500">{m.operation_id ? m.operation_id.slice(0, 8) : '—'}</td>
                  <td className="p-2 text-slate-500">{m.gestor_id ? m.gestor_id.slice(0, 8) : '—'}</td>
                  <td className="p-2 text-right font-mono text-slate-500">
                    {m.kind === 'intake'  ? `${m.currency_from || '—'} @ ${m.leg1_rate || '—'}` :
                     m.kind === 'outflow' ? `${m.currency_to   || '—'} @ ${m.leg2_rate || '—'}` :
                     '—'}
                  </td>
                  <td className="p-2 text-right font-mono">{m.delivered_local ? fmt(m.delivered_local) : '—'}</td>
                  <td className="p-2 text-slate-600 max-w-xs truncate" title={m.description}>{m.description}</td>
                  <td className="p-2 text-slate-400">{new Date(m.created_at).toLocaleString('es-ES')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Manual adjustment dialog */}
      <Dialog open={adjOpen} onOpenChange={setAdjOpen}>
        <DialogContent className="sm:max-w-md" data-testid="treasury-adjustment-dialog">
          <DialogHeader>
            <DialogTitle>Ajuste manual USDT</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Carga un ajuste signed (positivo acredita, negativo debita). Queda registrado en el ledger.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitAdj} className="space-y-3">
            <div>
              <label className="text-[10px] uppercase text-slate-500">Monto USDT (puede ser negativo)</label>
              <Input type="number" step="0.0001" value={adj.amount} onChange={(e) => setAdj({ ...adj, amount: e.target.value })} required data-testid="adj-amount" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-slate-500">Motivo</label>
              <Textarea rows={3} value={adj.reason} onChange={(e) => setAdj({ ...adj, reason: e.target.value })} required minLength={3} data-testid="adj-reason" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAdjOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={busy} className="bg-slate-900 hover:bg-slate-800" data-testid="adj-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null} Guardar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminTreasuryUSDTPage;
